import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { BiImageAlt } from 'react-icons/bi';
import { MdOutlineImage } from 'react-icons/md';

import { AuthorizedImage } from '@/components/AuthorizedImage';

import s from './inputs.module.scss';
import { usePhotoUpload } from './usePhotoUpload';

/** The picker's image box: full width of a form (about 430 px) by 300 px. */
export const LOGO_PICKER_WIDTH = 430;
export const LOGO_PICKER_HEIGHT = 300;

/**
 * The photo field of a form. Picking or dropping a photo opens the adjust dialog; Done hands the
 * photo to `onChange`. When `onChange` returns a promise, that is the upload — the photo shows as
 * pending, and a failure brings the previous one back with Retry.
 */
export const LogoPicker = ({
  onChange,
  initialLogo,
  round = true,
  compact = false,
  onCancel,
  onSave,
  ref,
}: {
  onChange: (l: File) => Promise<unknown> | void;
  initialLogo?: null | string;
  round?: boolean;
  compact?: boolean;
  /** After Done — once the upload has gone through, when `onChange` uploads. */
  onSave?(): void;
  /** The adjust dialog was dismissed. */
  onCancel?(): void;
  ref?: React.Ref<HTMLInputElement>;
}) => {
  // The photo picked here, kept on show once it is in; `initialLogo` is what was there before.
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => () => void (picked && URL.revokeObjectURL(picked)), [picked]);

  const upload = usePhotoUpload({
    shape: round ? 'round' : 'rect',
    inputRef: ref,
    onCancel,
    onPhoto: (file) => {
      const saved = onChange(file);
      if (!saved) {
        setPicked(URL.createObjectURL(file));
        onSave?.();
        return;
      }
      return saved.then(() => {
        setPicked(URL.createObjectURL(file));
        onSave?.();
      });
    },
  });

  const shown = upload.preview ?? picked ?? initialLogo;

  return (
    <div className={clsx(s.LogoPicker, round && s.round, compact && s.compact)}>
      <button
        type="button"
        className={s.pickerArea}
        aria-label={shown ? 'Change Photo' : 'Add Photo'}
        onClick={upload.pick}
        {...upload.dropProps}
      >
        {shown ? (
          <div className={s.imageWrapper}>
            <div className={s.imageHover}>
              <BiImageAlt size={56} aria-hidden="true" />
            </div>
            <AuthorizedImage src={shown} alt="" />
            {upload.overlay}
          </div>
        ) : (
          <div className={s.empty}>
            <MdOutlineImage aria-hidden="true" />
            <span>Photo or Logo</span>
            {upload.overlay}
          </div>
        )}
      </button>
      {upload.element}
    </div>
  );
};
